<template>
  <div class="nav-shell">
    <!-- BARRA SUPERIOR (solo móvil/tablet): abre el drawer modal -->
    <header class="topbar">
      <button
        ref="menuBtn"
        class="icon-btn"
        type="button"
        aria-label="Abrir menú de navegación"
        aria-controls="app-drawer"
        :aria-expanded="mobileOpen ? 'true' : 'false'"
        @click="openDrawer"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>
      <div class="topbar-title">
        <span class="topbar-app">Portal CX</span>
        <span class="topbar-section">{{ currentSectionLabel }}</span>
      </div>
    </header>

    <!-- SCRIM (solo con drawer modal abierto) -->
    <transition name="fade">
      <div
        v-if="mobileOpen"
        class="scrim"
        aria-hidden="true"
        @click="closeDrawer"
      ></div>
    </transition>

    <!-- NAVIGATION DRAWER -->
    <aside
      id="app-drawer"
      ref="drawer"
      class="drawer"
      :class="{ collapsed: isCollapsed, open: mobileOpen }"
      :role="isMobile ? 'dialog' : null"
      :aria-modal="isMobile && mobileOpen ? 'true' : null"
      aria-label="Navegación principal"
      :inert="isMobile && !mobileOpen ? true : null"
      @keydown.esc="closeDrawer"
    >
      <!-- Marca -->
      <div class="drawer-brand">
        <div class="brand-text">
          <span class="brand-title">Dashboard Ejecutivo</span>
          <span class="brand-sub">Portal CX</span>
        </div>
        <button
          v-if="isMobile"
          class="icon-btn close-btn"
          type="button"
          aria-label="Cerrar menú"
          @click="closeDrawer"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </div>

      <!-- Destinos principales -->
      <nav class="drawer-nav">
        <span class="nav-heading">Navegación</span>
        <ul>
          <li v-for="item in navItems" :key="item.to">
            <!-- "/" es prefijo de todas las rutas: se marca activa solo en
                 coincidencia exacta para que no quede resaltada en las demás. -->
            <router-link
              :to="item.to"
              class="nav-item"
              :active-class="item.exact ? '' : 'active'"
              exact-active-class="active"
              :title="isCollapsed ? item.label : null"
              @click="onNavigate"
            >
              <svg class="nav-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path v-for="(d, i) in item.icon" :key="i" :d="d" />
              </svg>
              <span class="nav-label">{{ item.label }}</span>
            </router-link>
          </li>
        </ul>
      </nav>

      <!-- Pie: estado, tema, usuario y salida -->
      <div class="drawer-footer">
        <div class="status" :title="isCollapsed ? 'Datos en vivo · ORDS' : null">
          <span class="dotlive"></span>
          <span class="nav-label">Datos en vivo · ORDS</span>
        </div>

        <button
          class="nav-item"
          type="button"
          role="switch"
          :aria-checked="isDark ? 'true' : 'false'"
          :title="isCollapsed ? (isDark ? 'Modo claro' : 'Modo oscuro') : null"
          @click="toggleTheme"
        >
          <svg class="nav-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path
              v-if="isDark"
              d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z"
            />
            <template v-else>
              <circle cx="12" cy="12" r="4" />
              <path
                d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"
              />
            </template>
          </svg>
          <span class="nav-label">{{ isDark ? "Modo oscuro" : "Modo claro" }}</span>
          <span class="switch nav-label" :class="{ on: isDark }"></span>
        </button>

        <div
          v-if="isLoggedIn"
          class="user"
          :title="isCollapsed ? userName : null"
        >
          <span class="avatar" aria-hidden="true">{{ userInitials }}</span>
          <span class="nav-label user-name">{{ userName }}</span>
        </div>

        <button
          class="nav-item logout"
          type="button"
          :title="isCollapsed ? 'Salir' : null"
          @click="handleLogout"
        >
          <svg class="nav-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
          </svg>
          <span class="nav-label">Salir</span>
        </button>

        <!-- Contraer / expandir (solo escritorio) -->
        <button
          v-if="!isMobile"
          class="nav-item collapse-btn"
          type="button"
          :aria-label="isCollapsed ? 'Expandir menú' : 'Contraer menú'"
          :aria-expanded="isCollapsed ? 'false' : 'true'"
          :title="isCollapsed ? 'Expandir menú' : null"
          @click="toggleCollapsed"
        >
          <svg class="nav-icon chevron" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" />
          </svg>
          <span class="nav-label">Contraer menú</span>
        </button>
      </div>
    </aside>
  </div>
</template>

<script>
import cxService from "../services/cx.service";

// Punto de quiebre: por debajo el drawer es modal (off-canvas).
const MOBILE_QUERY = "(max-width: 1023.98px)";
const DRAWER_W = { expanded: "264px", collapsed: "76px", mobile: "0px" };

// Destinos del menú. `roles` restringe a ciertos roles (null = todos).
const NAV_ITEMS = [
  {
    to: "/",
    label: "Dashboard",
    exact: true,
    icon: ["M3 3h7v9H3zM14 3h7v5h-7zM14 12h7v9h-7zM3 16h7v5H3z"],
  },
  {
    to: "/encuestas",
    label: "Encuestas",
    icon: [
      "M9 4h6v3H9z",
      "M15 5h3a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h3",
      "M9 12h6M9 16h4",
    ],
  },
  {
    to: "/clientes",
    label: "Contactos",
    icon: [
      "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2",
      "M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z",
      "M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75",
    ],
  },
  {
    to: "/portal",
    label: "Portal de seguimiento",
    icon: ["M22 12h-4l-3 9L9 3l-3 9H2"],
  },
  {
    to: "/acceso",
    label: "Acceso",
    roles: ["admin"],
    icon: [
      "M12 2a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-1V7a5 5 0 0 0-5-5zM9 7a3 3 0 0 1 6 0v3H9V7z",
    ],
  },
];

export default {
  name: "NavbarDashboard",

  data() {
    return {
      userName: "",
      isLoggedIn: true,
      rol: "",
      isDark: localStorage.getItem("theme") === "dark",
      isMobile: false,
      mobileOpen: false,
      isCollapsed: localStorage.getItem("navCollapsed") === "1",
      mql: null,
    };
  },

  computed: {
    navItems() {
      const r = this.rol;
      return NAV_ITEMS.filter((item) => {
        if (item.roles && item.roles.indexOf(r) === -1) return false;
        if (r === "gerente_area" && item.to !== "/portal") return false;
        return true;
      });
    },
    currentSectionLabel() {
      const path = this.$route.path;
      const item =
        this.navItems.find((i) => !i.exact && path.startsWith(i.to)) ||
        this.navItems.find((i) => i.to === path);
      return item ? item.label : "";
    },

    userInitials() {
      const parts = (this.userName || "").trim().split(/[\s@._-]+/);
      return (
        parts
          .filter(Boolean)
          .slice(0, 2)
          .map((p) => p[0].toUpperCase())
          .join("") || "U"
      );
    },
  },

  mounted() {
    // Verificar si el usuario está logueado
    this.checkAuthStatus();
    // Aplicar el tema guardado
    this.applyTheme();
    // Resolver el rol para mostrar el menú que corresponde
    this.cargarRol();

    this.mql = window.matchMedia(MOBILE_QUERY);
    this.mql.addEventListener("change", this.onMediaChange);
    this.onMediaChange(this.mql);
  },

  beforeUnmount() {
    if (this.mql) this.mql.removeEventListener("change", this.onMediaChange);
    document.body.style.overflow = "";
    document.documentElement.style.removeProperty("--drawer-w");
  },

  methods: {
    // ============ DRAWER ============
    onMediaChange(e) {
      this.isMobile = e.matches;
      if (!this.isMobile) this.mobileOpen = false;
      this.syncLayout();
    },

    /**
     * Publica el ancho ocupado por el drawer en --drawer-w para que el
     * contenido (App.vue) se desplace sin acoplarse a este componente.
     */
    syncLayout() {
      let w = DRAWER_W.expanded;
      if (this.isMobile) w = DRAWER_W.mobile;
      else if (this.isCollapsed) w = DRAWER_W.collapsed;
      document.documentElement.style.setProperty("--drawer-w", w);
      document.body.style.overflow =
        this.isMobile && this.mobileOpen ? "hidden" : "";
    },

    openDrawer() {
      this.mobileOpen = true;
      this.syncLayout();
      this.$nextTick(() => {
        const first = this.$refs.drawer.querySelector(".nav-item");
        if (first) first.focus();
      });
    },

    closeDrawer() {
      if (!this.mobileOpen) return;
      this.mobileOpen = false;
      this.syncLayout();
      this.$nextTick(() => this.$refs.menuBtn && this.$refs.menuBtn.focus());
    },

    onNavigate() {
      if (this.isMobile) this.closeDrawer();
    },

    toggleCollapsed() {
      this.isCollapsed = !this.isCollapsed;
      localStorage.setItem("navCollapsed", this.isCollapsed ? "1" : "0");
      this.syncLayout();
    },

    /**
     * Alterna entre modo claro y oscuro
     */
    toggleTheme() {
      this.isDark = !this.isDark;
      localStorage.setItem("theme", this.isDark ? "dark" : "light");
      this.applyTheme();
    },

    applyTheme() {
      document.documentElement.classList.toggle("dark", this.isDark);
    },

    /**
     * Resuelve el rol de la sesión (desde la base) para filtrar el menú.
     */
    async cargarRol() {
      try {
        const s = await cxService.sesion();
        this.rol = (s && s.rol) || "";
      } catch (e) {
        this.rol = "";
      }
    },

    /**
     * Verifica el estado de autenticación del usuario
     */
    checkAuthStatus() {
      const user = localStorage.getItem("token");
      if (user) {
        try {
          const userData = JSON.parse(user);
          this.userName = userData.name || userData.email || "Usuario";
          this.isLoggedIn = true;
        } catch (error) {
          console.error("Error al parsear datos del usuario:", error);
          this.isLoggedIn = false;
        }
      } else {
        this.isLoggedIn = false;
      }
    },

    /**
     * Maneja el cierre de sesión
     */
    handleLogout() {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      localStorage.removeItem("rol");
      // Limpieza de la sesión de pestaña
      sessionStorage.clear();

      // Redirigir al login
      window.location.reload();
    },
  },

  watch: {
    $route() {
      // Actualizar estado cuando cambie la ruta
      this.checkAuthStatus();
      if (this.isMobile) this.closeDrawer();
    },
  },
};
</script>

<style scoped>
/* ============================================
   DRAWER (escritorio: persistente / rail)
   ============================================ */
.drawer {
  position: fixed;
  inset: 0 auto 0 0;
  z-index: 60;
  width: 264px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 12px;
  background: var(--header-bg);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-right: 1px solid var(--border);
  box-shadow: var(--shadow-sm);
  overflow-x: hidden;
  overflow-y: auto;
  transition: width 0.25s cubic-bezier(0.16, 1, 0.3, 1),
    transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.drawer.collapsed {
  width: 76px;
}

.drawer-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 6px 16px;
  border-bottom: 1px solid var(--border);
  min-height: 60px;
}

.brand-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.2;
}

.brand-title {
  font-size: 16px;
  font-weight: 800;
  color: var(--txt);
  white-space: nowrap;
}

.brand-sub {
  font-size: 11px;
  color: var(--txt-faint);
  letter-spacing: 0.06em;
  font-weight: 500;
}

/* ============================================
   ITEMS DE NAVEGACIÓN
   ============================================ */
.drawer-nav {
  flex: 1;
  padding-top: 8px;
}

.drawer-nav ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.nav-heading {
  display: block;
  padding: 4px 12px 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--txt-faint);
  white-space: nowrap;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  min-height: 44px; /* área táctil mínima */
  padding: 10px 14px;
  border: none;
  border-radius: 12px;
  background: none;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  color: var(--txt-dim);
  text-align: left;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
}

.nav-item:hover {
  color: var(--txt);
  background: var(--surface-2);
}

.nav-item:focus-visible {
  outline: 2px solid var(--primary-light);
  outline-offset: 2px;
}

.nav-item.active {
  color: var(--on-accent);
  background: linear-gradient(135deg, var(--secondary), var(--primary));
  box-shadow: 0 2px 12px rgba(67, 178, 52, 0.25);
}

.nav-icon {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.nav-label {
  overflow: hidden;
  text-overflow: ellipsis;
  transition: opacity 0.2s ease;
}

/* ============================================
   PIE DEL DRAWER
   ============================================ */
.drawer-footer {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 12px;
  border-top: 1px solid var(--border);
}

.status,
.user {
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 40px;
  padding: 8px 14px;
  font-size: 12px;
  color: var(--txt-dim);
  white-space: nowrap;
}

.dotlive {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin: 0 7px;
  border-radius: 50%;
  background: var(--good);
  animation: pulse 2s infinite;
  flex-shrink: 0;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(67, 178, 52, 0.5);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(67, 178, 52, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(67, 178, 52, 0);
  }
}

.switch {
  position: relative;
  margin-left: auto;
  width: 36px;
  height: 20px;
  flex-shrink: 0;
  border-radius: 999px;
  background: var(--border-2);
  transition: background 0.25s ease;
}

.switch::after {
  content: "";
  position: absolute;
  top: 3px;
  left: 3px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  transition: left 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

.switch.on {
  background: var(--primary-light);
}

.switch.on::after {
  left: 19px;
}

.avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border-radius: 50%;
  font-size: 10px;
  font-weight: 800;
  color: var(--on-accent);
  background: linear-gradient(135deg, var(--secondary), var(--primary));
}

.user-name {
  font-weight: 700;
  color: var(--txt);
}

.logout:hover {
  color: var(--bad);
}

.chevron {
  transition: transform 0.25s ease;
}

/* ============================================
   ESTADO CONTRAÍDO (rail de iconos)
   ============================================ */
.drawer.collapsed .nav-label,
.drawer.collapsed .brand-text,
.drawer.collapsed .nav-heading {
  opacity: 0;
  width: 0;
  pointer-events: none;
}

.drawer.collapsed .nav-heading {
  height: 8px;
  padding: 0;
}

/* Sin logo, el encabezado no tiene nada que mostrar en el rail */
.drawer.collapsed .drawer-brand {
  display: none;
}

.drawer.collapsed .nav-item,
.drawer.collapsed .status,
.drawer.collapsed .user {
  justify-content: center;
  gap: 0;
  padding-left: 0;
  padding-right: 0;
}

.drawer.collapsed .chevron {
  transform: rotate(180deg);
}

/* ============================================
   BARRA SUPERIOR (móvil)
   ============================================ */
.topbar {
  display: none;
}

.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border: none;
  border-radius: 12px;
  background: none;
  color: var(--txt);
  cursor: pointer;
}

.icon-btn:hover {
  background: var(--surface-2);
}

.icon-btn:focus-visible {
  outline: 2px solid var(--primary-light);
  outline-offset: 2px;
}

.icon-btn svg {
  width: 24px;
  height: 24px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
}

.close-btn {
  margin-left: auto;
}

.scrim {
  position: fixed;
  inset: 0;
  z-index: 55;
  background: rgba(10, 22, 40, 0.45);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* ============================================
   RESPONSIVE: drawer modal off-canvas
   ============================================ */
@media (max-width: 1023.98px) {
  .topbar {
    position: sticky;
    top: 0;
    z-index: 50;
    display: flex;
    align-items: center;
    gap: 10px;
    height: 56px;
    padding: 0 12px;
    background: var(--header-bg);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border-bottom: 1px solid var(--border);
    box-shadow: var(--shadow-sm);
  }

  .topbar-title {
    display: flex;
    flex-direction: column;
    min-width: 0;
    line-height: 1.15;
  }

  .topbar-app {
    font-size: 11px;
    color: var(--txt-faint);
    letter-spacing: 0.06em;
  }

  .topbar-section {
    font-size: 16px;
    font-weight: 800;
    color: var(--txt);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .drawer,
  .drawer.collapsed {
    width: min(300px, 86vw);
    transform: translateX(-100%);
    background: var(--page-bg);
    box-shadow: none;
  }

  .drawer.open {
    transform: translateX(0);
    box-shadow: var(--shadow-lg);
  }

  /* En móvil nunca se muestra el rail contraído */
  .drawer.collapsed .nav-label,
  .drawer.collapsed .brand-text,
  .drawer.collapsed .nav-heading {
    opacity: 1;
    width: auto;
    pointer-events: auto;
  }

  .drawer.collapsed .nav-heading {
    height: auto;
    padding: 4px 12px 8px;
  }

  .drawer.collapsed .drawer-brand {
    display: flex;
    justify-content: flex-start;
    padding: 4px 6px 16px;
  }

  .drawer.collapsed .nav-item,
  .drawer.collapsed .status,
  .drawer.collapsed .user {
    justify-content: flex-start;
    gap: 14px;
    padding-left: 14px;
    padding-right: 14px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .drawer,
  .nav-label,
  .chevron,
  .switch::after,
  .fade-enter-active,
  .fade-leave-active {
    transition: none;
  }

  .dotlive {
    animation: none;
  }
}
</style>
